// feature-dispatch-gate-guard.ts
//
// Bug I (P15, found by the P14 real mission ME-2026-f3eabe): G0/G1 were
// requested at `autopilot init` and then NEVER read again before dispatch.
// The mission ran all 8 feature tickets and committed real code while
// gates.json still said `g0: pending, g1: pending`. Measured cause:
// `readGateFile` had 6 callers and none of them sat on the dispatch path —
// scheduler.ts contains no gate logic at all, and the word "gate" inside
// supervisor-dispatch-guards.ts refers only to the §18 throttle. In
// practice only G3 blocked anything; G0/G1/G2 were decorative.
//
// Spec rev 1.4 is explicit — §13: "Authority bootstrap chỉ cho metadata/
// setup local, không thực thi feature trước G0/G1", and the lifecycle reads
// DRAFTING -> G0 -> G1 -> SURVEYING* -> PLANNING -> G2* -> DELIVERING.
//
// Why NOT in supervisor-dispatch-guards.ts (`evaluateBeforeDispatch`): that
// gate is mission-wide and has no ticket in hand (it also runs once at
// startSupervisor time), but this decision is PER TICKET — the two seeds
// `autopilot init` writes (T-0001 survey, T-0002 plan) must still run,
// because they PRODUCE the very artifacts G0 and G1 approve. Gating them
// would deadlock every mission. So the guard lives at the one point that
// knows the selected ticket: supervisor-loop.ts, right after
// nextReadyTicket() and BEFORE journalAttemptStart(), so a blocked feature
// ticket never even gets an `attempt_started` record.
//
// G2 (P16, H4 2026-09-29): decided once the plan is ready (bootstrap done)
// and bound to the plan content hash; B2-5 makes it required for every
// mission in v1. The decision and the gates.json write live in
// g2-plan-gate-decision.ts / g2-dispatch-gate-enforcement.ts; this guard reads
// the record plus this tick's plan readiness (g2-feature-dispatch-check.ts).
// A stale comment about a gate is how the next reader concludes a hole is
// still open, or that a closed one never was — keep this one current.

import {
  APPROVED_GATE_STATUSES,
  REQUIRED_GATES,
  readGateStatusesStrict,
} from "./feature-dispatch-gate-file-reader.js";
import { recheckG0BlueprintBeforeDispatch } from "./g0-blueprint-recheck-before-dispatch.js";
import { isControllerOwnedReadOnlySession } from "./g2-controller-review-session-exemption.js";
import { checkG2BeforeFeatureDispatch } from "./g2-feature-dispatch-check.js";
import type { G2PlanReadiness } from "./g2-plan-readiness.js";
import { GATE_FILE_NAME } from "./gate-registry.js";
import { requireAnsweredQuestionIds } from "./interview-required-answers-gate.js";
import { INTERVIEW_FILE_NAME } from "./interview-store.js";
import type { TicketKind } from "./schemas/ticket-schema.js";

/**
 * Kinds that may dispatch BEFORE G0/G1 approval. Exactly the two seeds
 * `mission-initializer-tickets.ts` writes: they read source and draft the
 * blueprint/plan artifacts that G0/G1 then approve. Everything else either
 * writes product code, runs commands, or touches the outside world.
 * Exported for the cross-layer pin test (see `proposal-applier.ts` BOOTSTRAP_KINDS).
 */
export const BOOTSTRAP_TICKET_KINDS: readonly TicketKind[] = ["survey", "plan"];

export type FeatureDispatchGateBlockCode =
  | "gate_not_approved"
  | "gate_state_unreadable"
  | "interview_unanswered"
  /** H4: a feature ticket waits until the plan exists (bootstrap done). */
  | "g2_plan_not_ready";

export interface FeatureDispatchGateDecision {
  blocked: boolean;
  code?: FeatureDispatchGateBlockCode;
  /** Human-readable, safe to journal and print — names the gates, never any secret. */
  reason?: string;
}

export interface EvaluateFeatureDispatchGateGuardInput {
  controlRoot: string;
  /**
   * B3: the mission worktree, where the blueprint G0 was approved against is
   * re-hashed before every feature dispatch. Optional so callers without a
   * worktree in hand keep the pre-B3 decision; supervisor-loop.ts passes it.
   */
  repoDir?: string;
  ticketKind: TicketKind;
  /** Ticket id — used ONLY to recognise the controller's own reserved sessions (see g2-controller-review-session-exemption.ts's `isControllerOwnedReadOnlySession`). */
  ticketId?: string;
  /** Ticket's declared write scope. An empty scope is half the condition for the G2 exemption below. */
  allowedPaths?: readonly string[];
  /** H4: this tick's plan readiness from ensureG2DecisionRecorded. Absent ⇒ G2 not evaluated (pre-H4 rule). */
  g2PlanReadiness?: G2PlanReadiness;
}

export function isBootstrapTicketKind(kind: TicketKind): boolean {
  return BOOTSTRAP_TICKET_KINDS.includes(kind);
}

/**
 * Decides whether the ticket about to be dispatched is allowed to run given
 * the mission's recorded gate authority. Fail-CLOSED by construction: every
 * path that cannot PROVE authority returns blocked.
 */
export async function evaluateFeatureDispatchGateGuard(
  input: EvaluateFeatureDispatchGateGuardInput,
): Promise<FeatureDispatchGateDecision> {
  const statuses = await readGateStatusesStrict(input.controlRoot);

  // Checked BEFORE the bootstrap exemption on purpose: the exemption covers
  // a KNOWN pre-approval state (gates legitimately still pending on a fresh
  // mission), not an unreadable control file, which is a mission-integrity
  // problem regardless of ticket kind.
  if (statuses === undefined) {
    return {
      blocked: true,
      code: "gate_state_unreadable",
      reason:
        `không đọc được ${GATE_FILE_NAME} (thiếu, hỏng, hoặc status ngoài enum) — ` +
        "trạng thái thẩm quyền KHÔNG xác định nên chặn dispatch (fail-closed). Cần người xem xét.",
    };
  }

  if (isBootstrapTicketKind(input.ticketKind)) return { blocked: false };

  // B3 (plan 20260915-1650 phase-00): G0's approval is bound to the bytes of
  // BLUEPRINT.md. Re-hash before every feature dispatch; a change invalidates
  // and re-requests G0 (never auto-approves), an unreadable file fails closed.
  // Placed AFTER the bootstrap exemption: survey/plan produce the blueprint
  // and must never trip (or mutate) the gate they exist to feed.
  if (input.repoDir !== undefined) {
    const recheck = await recheckG0BlueprintBeforeDispatch({
      controlRoot: input.controlRoot,
      repoDir: input.repoDir,
    });
    if (recheck.kind === "unreadable") {
      return { blocked: true, code: "gate_state_unreadable", reason: recheck.reason };
    }
    if (recheck.kind === "invalidated") {
      return { blocked: true, code: "gate_not_approved", reason: recheck.reason };
    }
  }

  const missing = REQUIRED_GATES.filter((g) => !APPROVED_GATE_STATUSES.includes(statuses[g]));
  if (missing.length > 0) {
    const detail = missing.map((g) => `${g}=${statuses[g]}`).join(", ");
    return {
      blocked: true,
      code: "gate_not_approved",
      reason:
        `ticket kind "${input.ticketKind}" là feature nên cần G0+G1 đã duyệt, hiện ${detail} ` +
        "(spec §13: không thực thi feature trước G0/G1). Duyệt bằng `@avatar approve g0 <request-id>`.",
    };
  }

  // B4 (plan 20260915-1650 phase-04): a `required` question the human has not
  // answered yet holds feature dispatch. Placed AFTER the bootstrap exemption
  // and AFTER G0/G1 on purpose:
  //   - bootstrap (survey/plan) is what PRODUCES the questions, so gating it
  //     would mean nobody can ever ask anything (deadlock);
  //   - an authority failure is the more fundamental problem and should be the
  //     reason reported first.
  // Fail-OPEN, unlike every gate above it: a mission with no interview.json
  // (almost all of them today, including every e2e fixture) is NOT blocked.
  // See interview-required-answers-gate.ts's `requireAnsweredQuestionIds` for
  // that argument.
  // The mission does NOT auto-answer from defaults — that is the human's call
  // (phase-04 "KHÔNG làm"): the only way out is a real answer.
  // H1 (code-reviewer 260916 phase-06): truyền ticket để câu `needs_decision`
  // của ticket KHÁC không chặn ticket này. Câu viết tay bước Clarify vẫn chặn
  // toàn mission — xem `requireAnsweredQuestionIds` cho ranh giới hai loại.
  const unanswered = await requireAnsweredQuestionIds(input.controlRoot, input.ticketId);
  if (unanswered.length > 0) {
    return {
      blocked: true,
      code: "interview_unanswered",
      reason:
        `ticket kind "${input.ticketKind}" chờ câu trả lời cho câu hỏi required còn treo: ` +
        `${unanswered.join(", ")} (xem ${INTERVIEW_FILE_NAME}). ` +
        "Trả lời bằng `@avatar answer <question-id>: <giá trị>` — hệ thống KHÔNG tự suy câu trả lời.",
    };
  }

  // P20: the controller's own read-only review session skips G2 (and only
  // G2) — see g2-controller-review-session-exemption.ts for why, and why it
  // is not keyed on the ticket kind. Placed AFTER the G0/G1 check on
  // purpose: the exemption never covers the mission's basic authority to act.
  if (isControllerOwnedReadOnlySession(input)) return { blocked: false };

  // G2 (P16, H4) — see g2-feature-dispatch-check.ts.
  const g2Block = checkG2BeforeFeatureDispatch({
    ticketKind: input.ticketKind,
    ticketId: input.ticketId,
    g2Status: statuses.g2,
    g2PlanReadiness: input.g2PlanReadiness,
  });
  if (g2Block !== undefined) return g2Block;

  return { blocked: false };
}
